import { useState } from 'react';
import {
  FloppyDisk,
  Users,
  Package,
  Bell,
  ShieldCheck,
  CheckSquare,
  Square,
} from '@phosphor-icons/react';

interface SupplierSettingsState {
  notifyOnNewSupplier: boolean;
  notifyOnSupplierUpdate: boolean;
  notifyOnOrderAssignment: boolean;
  notifyOnFulfillment: boolean;
  autoApproveSuppliers: boolean;
  requireEmailVerification: boolean;
  requirePhoneVerification: boolean;
  defaultCanViewOrders: boolean;
  defaultCanFulfillOrders: boolean;
  defaultCanViewInventory: boolean;
  defaultCanViewProducts: boolean;
  allowSupplierOrderSelection: boolean;
  autoAssignOrders: boolean;
  assignmentStrategy: 'round_robin' | 'manual' | 'performance_based';
}

export default function SupplierSettingsPage() {
  const [settings, setSettings] = useState<SupplierSettingsState>({
    notifyOnNewSupplier: true,
    notifyOnSupplierUpdate: true,
    notifyOnOrderAssignment: true,
    notifyOnFulfillment: true,
    autoApproveSuppliers: false,
    requireEmailVerification: false,
    requirePhoneVerification: true,
    defaultCanViewOrders: true,
    defaultCanFulfillOrders: true,
    defaultCanViewInventory: false,
    defaultCanViewProducts: true,
    allowSupplierOrderSelection: false,
    autoAssignOrders: false,
    assignmentStrategy: 'manual',
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const updateSetting = <K extends keyof SupplierSettingsState>(key: K, value: SupplierSettingsState[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    setIsSaving(false);
    setIsDirty(false);
  };

  return (
    <div className="max-w-[800px] pb-20 space-y-6">
      {/* Save Bar */}
      {isDirty && (
        <div className="sticky top-4 z-50 flex items-center justify-between p-3.5 px-4 bg-[#202223] dark:bg-slate-800 text-white rounded-xl shadow-lg border border-white/10 animate-in slide-in-from-top-2 duration-150">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-400" />
            <span className="text-xs font-semibold">Unsaved supplier network settings</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsDirty(false)}
              className="px-3 py-1.5 text-xs font-semibold text-gray-300 hover:text-white transition-colors"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white text-[#202223] font-bold text-xs rounded-lg hover:bg-gray-100 transition-all disabled:opacity-50"
            >
              <FloppyDisk weight="fill" className="w-3.5 h-3.5" />
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="pb-4 border-b border-[#D2D5D9] dark:border-slate-800">
        <h1 className="text-xl font-bold text-[#202223] dark:text-white tracking-tight">Supplier network</h1>
        <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
          Configure partner onboarding rules, notification preferences, and automated order routing
        </p>
      </div>

      {/* Section 1: Alert Preferences */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-2xs space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-[#E1E3E5] dark:border-slate-800">
          <Bell weight="fill" className="w-4 h-4 text-[#5C5F62]" />
          <h2 className="text-sm font-bold text-[#202223] dark:text-white">Alert preferences</h2>
        </div>

        <div className="divide-y divide-[#E1E3E5] dark:divide-slate-800">
          {[
            { id: 'notifyOnNewSupplier', label: 'New vendor registrations', desc: 'Receive email notification when a supplier requests to join your network' },
            { id: 'notifyOnOrderAssignment', label: 'Order assignment dispatch', desc: 'Notify warehouse staff when orders are routed to supplier facilities' },
            { id: 'notifyOnFulfillment', label: 'Fulfillment confirmation', desc: 'Receive alert when suppliers mark assigned orders as shipped' },
          ].map((item) => {
            const isChecked = settings[item.id as keyof SupplierSettingsState] as boolean;
            return (
              <div
                key={item.id}
                onClick={() => updateSetting(item.id as keyof SupplierSettingsState, !isChecked)}
                className="py-3 flex items-center justify-between cursor-pointer group"
              >
                <div>
                  <p className="text-xs font-semibold text-[#202223] dark:text-white">{item.label}</p>
                  <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">{item.desc}</p>
                </div>
                <button type="button" className="text-[#202223] dark:text-white">
                  {isChecked ? (
                    <CheckSquare weight="fill" className="w-5 h-5 text-[#202223] dark:text-white" />
                  ) : (
                    <Square weight="bold" className="w-5 h-5 text-[#9EA3A7]" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Section 2: Onboarding & Governance */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-2xs space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-[#E1E3E5] dark:border-slate-800">
          <ShieldCheck weight="fill" className="w-4 h-4 text-[#5C5F62]" />
          <h2 className="text-sm font-bold text-[#202223] dark:text-white">Onboarding & compliance rules</h2>
        </div>

        <div className="divide-y divide-[#E1E3E5] dark:divide-slate-800">
          {[
            { id: 'autoApproveSuppliers', label: 'Auto-approve suppliers', desc: 'Automatically grant portal access without manual review' },
            { id: 'requireEmailVerification', label: 'Require verified email', desc: 'Vendors must verify business email prior to receiving purchase orders' },
            { id: 'requirePhoneVerification', label: 'Require SMS 2FA phone number', desc: 'Mandatory phone verification for warehouse fulfillment dispatch' },
          ].map((item) => {
            const isChecked = settings[item.id as keyof SupplierSettingsState] as boolean;
            return (
              <div
                key={item.id}
                onClick={() => updateSetting(item.id as keyof SupplierSettingsState, !isChecked)}
                className="py-3 flex items-center justify-between cursor-pointer group"
              >
                <div>
                  <p className="text-xs font-semibold text-[#202223] dark:text-white">{item.label}</p>
                  <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">{item.desc}</p>
                </div>
                <button type="button" className="text-[#202223] dark:text-white">
                  {isChecked ? (
                    <CheckSquare weight="fill" className="w-5 h-5 text-[#202223] dark:text-white" />
                  ) : (
                    <Square weight="bold" className="w-5 h-5 text-[#9EA3A7]" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Section 3: Default Supplier Permissions */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-2xs space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-[#E1E3E5] dark:border-slate-800">
          <Users weight="fill" className="w-4 h-4 text-[#5C5F62]" />
          <h2 className="text-sm font-bold text-[#202223] dark:text-white">Default portal permissions</h2>
        </div>

        <div className="divide-y divide-[#E1E3E5] dark:divide-slate-800">
          {[
            { id: 'defaultCanViewOrders', label: 'View assigned orders', desc: 'Suppliers can access shipping addresses and packing slips for their items' },
            { id: 'defaultCanFulfillOrders', label: 'Fulfill & add tracking', desc: 'Suppliers can enter tracking numbers and update item fulfillment state' },
            { id: 'defaultCanViewInventory', label: 'View global inventory levels', desc: 'Allow suppliers to view warehouse safety stock numbers across channels' },
            { id: 'defaultCanViewProducts', label: 'View catalog pricing', desc: 'Suppliers can view retail price and product attributes' },
          ].map((item) => {
            const isChecked = settings[item.id as keyof SupplierSettingsState] as boolean;
            return (
              <div
                key={item.id}
                onClick={() => updateSetting(item.id as keyof SupplierSettingsState, !isChecked)}
                className="py-3 flex items-center justify-between cursor-pointer group"
              >
                <div>
                  <p className="text-xs font-semibold text-[#202223] dark:text-white">{item.label}</p>
                  <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">{item.desc}</p>
                </div>
                <button type="button" className="text-[#202223] dark:text-white">
                  {isChecked ? (
                    <CheckSquare weight="fill" className="w-5 h-5 text-[#202223] dark:text-white" />
                  ) : (
                    <Square weight="bold" className="w-5 h-5 text-[#9EA3A7]" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Section 4: Automated Order Routing */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-2xs space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-[#E1E3E5] dark:border-slate-800">
          <Package weight="fill" className="w-4 h-4 text-[#5C5F62]" />
          <h2 className="text-sm font-bold text-[#202223] dark:text-white">Automated order routing</h2>
        </div>

        <div className="space-y-4">
          <div
            onClick={() => updateSetting('autoAssignOrders', !settings.autoAssignOrders)}
            className="flex items-center justify-between cursor-pointer"
          >
            <div>
              <p className="text-xs font-semibold text-[#202223] dark:text-white">Enable automated order assignment</p>
              <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
                Automatically assign line items to preferred suppliers upon checkout
              </p>
            </div>
            <button type="button" className="text-[#202223] dark:text-white">
              {settings.autoAssignOrders ? (
                <CheckSquare weight="fill" className="w-5 h-5 text-[#202223] dark:text-white" />
              ) : (
                <Square weight="bold" className="w-5 h-5 text-[#9EA3A7]" />
              )}
            </button>
          </div>

          {settings.autoAssignOrders && (
            <div className="pt-3 border-t border-[#E1E3E5] dark:border-slate-800">
              <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1.5">
                Assignment strategy
              </label>
              <select
                value={settings.assignmentStrategy}
                onChange={(e) => updateSetting('assignmentStrategy', e.target.value as any)}
                className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-900 text-xs text-[#202223] dark:text-white focus:outline-none"
              >
                <option value="manual">Manual routing only</option>
                <option value="round_robin">Round-robin balanced allocation</option>
                <option value="performance_based">Performance & lowest fulfillment time</option>
              </select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
